import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    signal
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MultiSelectFilterOption } from './multi-select-filter.model';

@Component({
    selector: 'app-multi-select-filter',
    templateUrl: './multi-select-filter.component.html',
    imports: [TranslatePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MultiSelectFilterComponent<T> {
    private translate = inject(TranslateService);
    private elementRef = inject(ElementRef);
    private destroyRef = inject(DestroyRef);

    public label = input.required<string>();
    public optionTranslationPrefix = input<string>('');
    public options = input.required<MultiSelectFilterOption<T>[]>();
    public targetId = input<string>('');
    public value = input<T[]>([]);
    public searchable = input<boolean>(false);
    public searchPlaceholder = input<string>('');
    /** Translation key shown as the badge when nothing is selected, e.g. to label an implicit "all" default. */
    public emptyStateLabel = input<string>('');

    public valueChange = output<T[]>();

    protected selectedValues = this.value;
    protected searchTerm = signal('');

    protected filteredOptions = computed<MultiSelectFilterOption<T>[]>(() => {
        const term = this.searchTerm().trim().toLowerCase();
        if (!term) return this.options();
        const prefix = this.optionTranslationPrefix();
        return this.options().filter((option) => {
            const key = prefix ? [prefix, option.label].join('.') : option.label;
            const translated = this.translate.instant(key) ?? '';
            return translated.toLowerCase().includes(term);
        });
    });

    constructor() {
        // sh-popover registers a capture-phase click listener on document.body that closes
        // the popover whenever a click happens outside its trigger. Clicks inside the search
        // input would therefore close the popover before the user can type. Intercept such
        // clicks in the capture phase on document (an ancestor of body, so it runs first) and
        // stop propagation so they never reach the popover's close handler.
        const captureClick = (event: Event): void => {
            const searchContainer = this.elementRef.nativeElement.querySelector('.multi-select-search');
            if (!searchContainer) {
                return;
            }
            const path = event.composedPath();
            if (!path.includes(searchContainer)) {
                return;
            }
            event.stopPropagation();
            // The clear icon of sh-input-text lives in its shadow root and reacts to a click
            // listener on the icon itself, which never fires because propagation is stopped
            // above. Reset the search term here so the clear button keeps working.
            if (path.some((node) => node instanceof Element && node.id === 'clear')) {
                this.searchTerm.set('');
            }
        };
        document.addEventListener('click', captureClick, { capture: true });
        this.destroyRef.onDestroy(() => document.removeEventListener('click', captureClick, { capture: true }));
    }

    protected onSearchInput(event: Event): void {
        const value = (event.target as HTMLInputElement | null)?.value ?? '';
        this.searchTerm.set(value);
    }

    /** sh-popover reads Space/Enter inside it as a menu item activation and closes itself. */
    protected onSearchKeyUp(event: KeyboardEvent): void {
        if (event.key === ' ' || event.code === 'Space' || event.key === 'Enter') {
            event.stopPropagation();
        }
    }

    protected toggleItem(value: T): void {
        const currentSelected = this.selectedValues();
        let newSelected: T[];

        const index = currentSelected.findIndex((v) => this.valuesEqual(v, value));
        if (index > -1) {
            newSelected = currentSelected.filter((_, i) => i !== index);
        } else {
            newSelected = [...currentSelected, value];
        }

        this.valueChange.emit(newSelected);
    }

    /** Only primitive option values make a stable test id; object options (e.g. teams) get none. */
    protected optionTestId(optionValue: T): string | null {
        return typeof optionValue === 'string' || typeof optionValue === 'number' ? `option-${optionValue}` : null;
    }

    protected isOptionActive(optionValue: T): boolean {
        return this.selectedValues().some((v) => this.valuesEqual(v, optionValue));
    }

    private valuesEqual(a: T, b: T): boolean {
        if (a === b) return true;
        if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
            return JSON.stringify(a) === JSON.stringify(b);
        }
        return false;
    }
}
